import { useState } from 'react';
import { ChevronDown, Sparkles } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface ThinkingSection {
  title: string;
  content: string;
}

interface Props {
  sections: ThinkingSection[];
  /** True while SSE is still streaming — drives the pulsing sparkle header */
  streaming?: boolean;
  defaultExpanded?: boolean;
}

/**
 * Displays "thinking" sections as they arrive from SSE.
 *
 * Rendering policy:
 *   - Show each section's content IMMEDIATELY as it arrives, with no
 *     word-by-word typing animation. The typing effect was lagging behind
 *     real progress: by the time "compilation" words finished rendering,
 *     the actual answer had already arrived. We dump content live instead.
 *   - Keep the visual design matching frontend-prod: sparkle icon with
 *     pulse on streaming, italic section titles, left connector border,
 *     collapsible accordion.
 */
export function ThinkingAccordion({
  sections,
  streaming = false,
  defaultExpanded = true,
}: Props): JSX.Element | null {
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (!sections.length) return null;

  return (
    <div className="my-3 text-sm">
      <button
        type="button"
        onClick={() => setExpanded((x) => !x)}
        className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
      >
        {streaming ? (
          <span className="relative inline-flex h-4 w-4">
            <span className="absolute inset-0 animate-ping opacity-30">
              <Sparkles className="h-4 w-4 text-primary" />
            </span>
            <Sparkles className="h-4 w-4 text-primary animate-pulse" />
          </span>
        ) : (
          <Sparkles className="h-3.5 w-3.5" />
        )}
        <span className="text-xs font-medium">{streaming ? 'Thinking…' : 'Thinking'}</span>
        <span className="text-[11px] text-muted-foreground">
          · {sections.length} step{sections.length === 1 ? '' : 's'}
        </span>
        <ChevronDown
          className={cn('w-3.5 h-3.5 transition-transform ml-1', expanded && 'rotate-180')}
        />
      </button>

      {expanded && (
        <div className="mt-2 space-y-3">
          {sections.map((section, i) => (
            <div key={section.title + ':' + i} className="relative pl-4">
              <div className="absolute left-0 top-0 bottom-0 w-px bg-border" />
              <div className="text-xs">
                <div className="italic font-medium text-muted-foreground mb-1">
                  {section.title}
                </div>
                <div className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
                  {section.content}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
